import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, FileText, Copy, Edit, Check, Plus, Trash2, Star, Loader } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import type {
  LifecycleTemplateView,
  LifecycleTemplateInput,
  OnboardingTaskTypeView,
  OffboardingTaskTypeView,
  MovementTaskTypeView,
} from '../../../../models/employee';
import { TemplateCategory } from '../../../../models/employee';
import * as templateApi from '../../../../api/Employee/lifecycleTemplates';
import { getOnboardingTaskTypes, getOffboardingTaskTypes, getMovementTaskTypes } from '../../../../api/Employee/lifecycleSettings';

// Local editing item with temp id for new items
interface EditingItem {
  _tempId: string;
  id?: number;
  name: string;
  typeId?: number;
  mandatory: boolean;
  isBlocking: boolean;
  isPolicyAcceptanceTask: boolean;
  defaultOwner: string;
  dueDateOffset: number;
  sortOrder: number;
}

interface EditingTemplate {
  id?: number;
  name: string;
  description: string;
  isDefault: boolean;
  items: EditingItem[];
}

interface TemplateManagerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  category: TemplateCategory;
}

function toEditingTemplate(t: LifecycleTemplateView): EditingTemplate {
  return {
    id: t.id,
    name: t.name,
    description: t.description || '',
    isDefault: t.isDefault,
    items: t.items.map((item) => ({
      _tempId: `existing_${item.id}`,
      id: item.id,
      name: item.name,
      typeId: item.typeId,
      mandatory: item.mandatory,
      isBlocking: item.isBlocking ?? false,
      isPolicyAcceptanceTask: item.isPolicyAcceptanceTask ?? false,
      defaultOwner: item.defaultOwner,
      dueDateOffset: item.dueDateOffset,
      sortOrder: item.sortOrder,
    })),
  };
}

export default function TemplateManagerPanel({
  isOpen,
  onClose,
  category,
}: TemplateManagerPanelProps) {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [templates, setTemplates] = useState<LifecycleTemplateView[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskTypes, setTaskTypes] = useState<{ id: number; name: string }[]>([]);

  const [selectedTemplate, setSelectedTemplate] = useState<LifecycleTemplateView | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState<EditingTemplate | null>(null);

  const categoryLabel = category === TemplateCategory.Onboarding
    ? t('Onboarding')
    : category === TemplateCategory.Offboarding
      ? t('Offboarding')
      : t('Movement');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await templateApi.getTemplates(category);
      if (result.errors?.length === 0) {
        setTemplates(result.payload || []);
      } else {
        showToast(t('Failed to load templates'), 'error');
      }
    } catch {
      showToast(t('Failed to load templates'), 'error');
    } finally {
      setLoading(false);
    }
  }, [category, showToast, t]);

  const fetchTaskTypes = useCallback(async () => {
    try {
      if (category === TemplateCategory.Onboarding) {
        const result = await getOnboardingTaskTypes();
        if (result.errors?.length === 0) {
          setTaskTypes((result.payload || []).filter((tt: OnboardingTaskTypeView) => tt.isActive).map((tt: OnboardingTaskTypeView) => ({ id: tt.id, name: tt.name })));
        }
      } else if (category === TemplateCategory.Offboarding) {
        const result = await getOffboardingTaskTypes();
        if (result.errors?.length === 0) {
          setTaskTypes((result.payload || []).filter((tt: OffboardingTaskTypeView) => tt.isActive).map((tt: OffboardingTaskTypeView) => ({ id: tt.id, name: tt.name })));
        }
      } else if (category === TemplateCategory.Movement) {
        const result = await getMovementTaskTypes();
        if (result.errors?.length === 0) {
          setTaskTypes((result.payload || []).filter((tt: MovementTaskTypeView) => tt.isActive).map((tt: MovementTaskTypeView) => ({ id: tt.id, name: tt.name })));
        }
      }
    } catch {
      // Task types are optional; silently fail
    }
  }, [category]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchTaskTypes();
      setSelectedTemplate(null);
      setIsEditing(false);
      setIsCreating(false);
      setEditedTemplate(null);
    }
  }, [isOpen, fetchTemplates, fetchTaskTypes]);

  const handleSelectTemplate = (template: LifecycleTemplateView) => {
    setSelectedTemplate(template);
    setEditedTemplate(toEditingTemplate(template));
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setIsCreating(true);
    setIsEditing(true);
    setEditedTemplate({
      name: '',
      description: '',
      isDefault: templates.length === 0,
      items: [],
    });
  };

  const handleSave = async () => {
    if (!editedTemplate || !editedTemplate.name.trim()) {
      showToast(t('Template name is required'), 'error');
      return;
    }

    setSaving(true);
    try {
      const input: LifecycleTemplateInput = {
        name: editedTemplate.name,
        description: editedTemplate.description || undefined,
        isDefault: editedTemplate.isDefault,
        templateCategory: category,
        items: editedTemplate.items.map((item, index) => ({
          name: item.name,
          typeId: item.typeId,
          mandatory: item.mandatory,
          isBlocking: item.isBlocking,
          isPolicyAcceptanceTask: item.isPolicyAcceptanceTask,
          defaultOwner: item.defaultOwner,
          dueDateOffset: item.dueDateOffset,
          sortOrder: index,
        })),
      };

      let result;
      if (isCreating) {
        result = await templateApi.createTemplate(input);
      } else if (selectedTemplate) {
        result = await templateApi.updateTemplate(selectedTemplate.id, input);
      }

      if (result && result.errors?.length === 0) {
        showToast(isCreating ? t('Template created successfully') : t('Template updated successfully'), 'success');
        await fetchTemplates();
        if (result.payload) {
          setSelectedTemplate(result.payload);
          setEditedTemplate(toEditingTemplate(result.payload));
        }
        setIsEditing(false);
        setIsCreating(false);
      } else {
        showToast(result?.errors?.[0]?.message || t('Failed to save template'), 'error');
      }
    } catch {
      showToast(t('Failed to save template'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
      setIsEditing(false);
      setEditedTemplate(null);
      return;
    }
    if (selectedTemplate) {
      setEditedTemplate(toEditingTemplate(selectedTemplate));
    }
    setIsEditing(false);
  };

  const handleAddItem = () => {
    if (!editedTemplate) return;
    const newItem: EditingItem = {
      _tempId: `new_${Date.now()}`,
      name: '',
      mandatory: false,
      isBlocking: false,
      isPolicyAcceptanceTask: false,
      defaultOwner: '',
      dueDateOffset: 0,
      sortOrder: editedTemplate.items.length,
    };
    setEditedTemplate({
      ...editedTemplate,
      items: [...editedTemplate.items, newItem],
    });
  };

  const handleRemoveItem = (tempId: string) => {
    if (!editedTemplate) return;
    setEditedTemplate({
      ...editedTemplate,
      items: editedTemplate.items.filter(item => item._tempId !== tempId),
    });
  };

  const handleUpdateItem = (tempId: string, field: keyof EditingItem, value: string | boolean | number | undefined) => {
    if (!editedTemplate) return;
    setEditedTemplate({
      ...editedTemplate,
      items: editedTemplate.items.map(item =>
        item._tempId === tempId ? { ...item, [field]: value } : item
      ),
    });
  };

  const handleToggleTaskType = (taskType: { id: number; name: string }, isOn: boolean) => {
    if (!editedTemplate) return;
    if (isOn) {
      const newItem: EditingItem = {
        _tempId: `type_${taskType.id}_${Date.now()}`,
        name: taskType.name,
        typeId: taskType.id,
        mandatory: false,
        isBlocking: false,
        isPolicyAcceptanceTask: false,
        defaultOwner: '',
        dueDateOffset: 0,
        sortOrder: editedTemplate.items.length,
      };
      setEditedTemplate({
        ...editedTemplate,
        items: [...editedTemplate.items, newItem],
      });
    } else {
      setEditedTemplate({
        ...editedTemplate,
        items: editedTemplate.items.filter(item => item.typeId !== taskType.id),
      });
    }
  };

  const handleDuplicate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const result = await templateApi.duplicateTemplate(selectedTemplate.id, {
        newName: `${selectedTemplate.name} (${t('Copy')})`,
      });
      if (result.errors?.length === 0) {
        showToast(t('Template duplicated successfully'), 'success');
        await fetchTemplates();
        if (result.payload) {
          setSelectedTemplate(result.payload);
          setEditedTemplate(toEditingTemplate(result.payload));
        }
      } else {
        showToast(result?.errors?.[0]?.message || t('Failed to duplicate template'), 'error');
      }
    } catch {
      showToast(t('Failed to duplicate template'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const result = await templateApi.deleteTemplate(selectedTemplate.id);
      if (result.errors?.length === 0) {
        showToast(t('Template deleted successfully'), 'success');
        setSelectedTemplate(null);
        setEditedTemplate(null);
        setIsEditing(false);
        await fetchTemplates();
      } else {
        showToast(result?.errors?.[0]?.message || t('Failed to delete template'), 'error');
      }
    } catch {
      showToast(t('Failed to delete template'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const result = await templateApi.setDefaultTemplate(selectedTemplate.id);
      if (result.errors?.length === 0) {
        showToast(t('Default template updated'), 'success');
        await fetchTemplates();
        setSelectedTemplate(prev => prev ? { ...prev, isDefault: true } : null);
      } else {
        showToast(result?.errors?.[0]?.message || t('Failed to set default template'), 'error');
      }
    } catch {
      showToast(t('Failed to set default template'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const displayItems = isEditing ? editedTemplate?.items : selectedTemplate?.items?.map(item => ({
    _tempId: `existing_${item.id}`,
    ...item,
  }));

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />
      {/* Slide-over Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-5xl bg-white shadow-2xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {categoryLabel} {t('Templates')}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t('Configure templates for')} {categoryLabel.toLowerCase()} {t('processes')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-start space-x-2 flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            <span className="font-semibold">{t('Note')}:</span> {t('Editing templates does NOT affect active processes. Changes only apply to new processes created after saving.')}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side - Template List */}
          <div className="w-80 border-r border-border bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-4 space-y-2">
              <button
                onClick={handleCreateNew}
                className="w-full text-left p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary hover:bg-white transition-all text-sm text-gray-600 hover:text-primary flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('Create New Template')}
              </button>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  {t('No templates found')}
                </div>
              ) : (
                templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-primary bg-white shadow-sm'
                        : 'border-transparent bg-white hover:border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-gray-900">{template.name}</div>
                      {template.isDefault && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {t('Default')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{template.description}</p>
                    <div className="text-xs text-gray-500">
                      {template.items?.length || 0} {t('checklist items')}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right Side - Template Editor */}
          <div className="flex-1 overflow-y-auto">
            {(selectedTemplate || isCreating) ? (
              <div className="p-6">
                {/* Template Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedTemplate?.name || ''}
                          onChange={(e) => editedTemplate && setEditedTemplate({ ...editedTemplate, name: e.target.value })}
                          placeholder={t('Template Name')}
                          className="text-xl font-semibold text-gray-900 border-b-2 border-primary focus:outline-none w-full"
                        />
                      ) : (
                        <h4 className="text-xl font-semibold text-gray-900">{selectedTemplate?.name}</h4>
                      )}
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedTemplate?.description || ''}
                          onChange={(e) => editedTemplate && setEditedTemplate({ ...editedTemplate, description: e.target.value })}
                          placeholder={t('Template Description')}
                          className="text-sm text-gray-600 mt-1 w-full border-b border-border focus:outline-none focus:border-primary"
                        />
                      ) : (
                        <p className="text-sm text-gray-600 mt-1">{selectedTemplate?.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                      {!isEditing ? (
                        <>
                          {selectedTemplate && !selectedTemplate.isDefault && (
                            <button
                              onClick={handleSetDefault}
                              disabled={saving}
                              className="px-3 py-1.5 text-sm border border-border rounded-md text-gray-700 hover:bg-gray-50"
                            >
                              <Star className="w-3.5 h-3.5 inline mr-1" />
                              {t('Set as Default')}
                            </button>
                          )}
                          <button
                            onClick={handleDuplicate}
                            disabled={saving}
                            className="px-3 py-1.5 text-sm border border-border rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            <Copy className="w-3.5 h-3.5 inline mr-1" />
                            {t('Duplicate')}
                          </button>
                          <button
                            onClick={handleEdit}
                            className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90"
                          >
                            <Edit className="w-3.5 h-3.5 inline mr-1" />
                            {t('Edit')}
                          </button>
                          <button
                            onClick={handleDelete}
                            disabled={saving}
                            className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                            {t('Delete')}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleCancel}
                            className="px-3 py-1.5 text-sm border border-border rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            {t('Cancel')}
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            {saving ? (
                              <Loader className="w-3.5 h-3.5 inline mr-1 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 inline mr-1" />
                            )}
                            {t('Save Changes')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Task Types / Checklist Items */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-semibold text-gray-900">
                      {isEditing && taskTypes.length > 0 ? t('Task Types') : t('Checklist Items')}
                    </h5>
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      {/* Task type toggle cards */}
                      {taskTypes.length > 0 && taskTypes.map((taskType) => {
                        const matchingItem = editedTemplate?.items.find(item => item.typeId === taskType.id);
                        const isIncluded = !!matchingItem;
                        return (
                          <div key={taskType.id} className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">{taskType.name}</span>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isIncluded}
                                onClick={() => handleToggleTaskType(taskType, !isIncluded)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isIncluded ? 'bg-primary' : 'bg-gray-200'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isIncluded ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            {isIncluded && matchingItem && (
                              <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-3">
                                {/* Mandatory */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Mandatory')}</label>
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={matchingItem.mandatory}
                                      onChange={(e) => handleUpdateItem(matchingItem._tempId, 'mandatory', e.target.checked)}
                                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                    />
                                    <span className="text-sm text-gray-700">{t('Required')}</span>
                                  </label>
                                </div>
                                {/* Blocking */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Blocking')}</label>
                                  <label className="flex items-center space-x-2 cursor-pointer" title={t('When checked, the employee cannot access the system until this task is completed')}>
                                    <input
                                      type="checkbox"
                                      data-testid={`template-item-${matchingItem._tempId}-isBlocking`}
                                      checked={matchingItem.isBlocking}
                                      onChange={(e) => handleUpdateItem(matchingItem._tempId, 'isBlocking', e.target.checked)}
                                      className="w-4 h-4 text-rose-600 border-border rounded focus:ring-rose-600"
                                    />
                                    <span className="text-sm text-gray-700">{t('Block access')}</span>
                                  </label>
                                </div>
                                {/* Default Owner */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Default Owner')}</label>
                                  <input
                                    type="text"
                                    value={matchingItem.defaultOwner}
                                    onChange={(e) => handleUpdateItem(matchingItem._tempId, 'defaultOwner', e.target.value)}
                                    placeholder={t('Role/Person')}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>
                                {/* Due Date Offset */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Due Date Offset')}</label>
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="number"
                                      value={matchingItem.dueDateOffset}
                                      onChange={(e) => handleUpdateItem(matchingItem._tempId, 'dueDateOffset', parseInt(e.target.value) || 0)}
                                      className="w-16 px-2 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                    <span className="text-xs text-gray-500">{t('days')}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Custom items (no typeId) */}
                      {editedTemplate?.items.filter(item => !item.typeId).map((item, index) => (
                        <div key={item._tempId} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-sm font-medium text-gray-600">
                              {index + 1}
                            </div>
                            <div className="flex-1 space-y-3">
                              {/* Item Name */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t('Task Name')}</label>
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => handleUpdateItem(item._tempId, 'name', e.target.value)}
                                  placeholder={t('Enter task name')}
                                  className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {/* Mandatory Toggle */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Mandatory')}</label>
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={item.mandatory}
                                      onChange={(e) => handleUpdateItem(item._tempId, 'mandatory', e.target.checked)}
                                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                    />
                                    <span className="text-sm text-gray-700">{t('Required')}</span>
                                  </label>
                                </div>
                                {/* Blocking Toggle */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Blocking')}</label>
                                  <label className="flex items-center space-x-2 cursor-pointer" title={t('When checked, the employee cannot access the system until this task is completed')}>
                                    <input
                                      type="checkbox"
                                      data-testid={`template-item-${item._tempId}-isBlocking`}
                                      checked={item.isBlocking}
                                      onChange={(e) => handleUpdateItem(item._tempId, 'isBlocking', e.target.checked)}
                                      className="w-4 h-4 text-rose-600 border-border rounded focus:ring-rose-600"
                                    />
                                    <span className="text-sm text-gray-700">{t('Block access')}</span>
                                  </label>
                                </div>

                                {/* Default Owner */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Default Owner')}</label>
                                  <input
                                    type="text"
                                    value={item.defaultOwner}
                                    onChange={(e) => handleUpdateItem(item._tempId, 'defaultOwner', e.target.value)}
                                    placeholder={t('Role/Person')}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>

                                {/* Due Date Offset */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Due Date Offset')}</label>
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="number"
                                      value={item.dueDateOffset}
                                      onChange={(e) => handleUpdateItem(item._tempId, 'dueDateOffset', parseInt(e.target.value) || 0)}
                                      className="w-16 px-2 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                    <span className="text-xs text-gray-500">{t('days')}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveItem(item._tempId)}
                              className="flex-shrink-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Divider and Add Custom Task button */}
                      <div className="border-t border-border pt-3">
                        <button
                          onClick={handleAddItem}
                          className="text-sm text-primary hover:text-primary/80 font-medium"
                        >
                          <Plus className="w-3.5 h-3.5 inline mr-1" />
                          {t('Add Custom Task')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {displayItems?.map((item, index) => (
                        <div key={item._tempId} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-sm font-medium text-gray-600">
                              {index + 1}
                            </div>
                            <div className="flex-1 space-y-3">
                              {/* Item Name */}
                              <div className="text-sm font-medium text-gray-900">{item.name}</div>

                              <div className="grid grid-cols-3 gap-3">
                                {/* Mandatory */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Mandatory')}</label>
                                  <div className="text-sm">
                                    {item.mandatory ? (
                                      <span className="text-red-600 font-medium">{t('Yes')}</span>
                                    ) : (
                                      <span className="text-gray-500">{t('No')}</span>
                                    )}
                                  </div>
                                </div>

                                {/* Default Owner */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Default Owner')}</label>
                                  <div className="text-sm text-gray-600">{item.defaultOwner || '-'}</div>
                                </div>

                                {/* Due Date Offset */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Due Date Offset')}</label>
                                  <div className="text-sm text-gray-600">+{item.dueDateOffset} {t('days')}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {(!displayItems || displayItems.length === 0) && (
                        <div className="text-center py-8 text-sm text-gray-500">
                          {t('No checklist items. Click Edit to add items.')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm">{t('Select a template to view or edit')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
}
